import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid";
import { Button, TextField } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import Pagination from "@mui/material/Pagination";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import classes from "../LandedCostUploadHistoryTable/LandedCostUploadHistory.module.css";
import { exportRowsToXlsx } from "../utils/exportUtils";

const normalizeContainerRateRow = (row = {}, index = 0) => {
  const id = row.id ?? row.uuid ?? row.recordId ?? `${row.fobPort ?? "container"}-${index}`;
  return {
    id,
    ...row,
    fobPort: row.fobPort ?? row.port ?? row.originPort ?? row.origin_port ?? row.pol ?? "",
    rate20: row.rate20 ?? row.rate_20 ?? row.rate20Ft ?? row.rate20ft ?? row.rate20In ?? "",
    rate40: row.rate40 ?? row.rate_40 ?? row.rate40Ft ?? row.rate40ft ?? row.rate40In ?? "",
    effectiveDate: row.effectiveDate ?? row.date ?? row.effective_date ?? "",
  };
};

const LandedCostContainerRateTable = ({ rows = [], loading = false }) => {
  const [pageSize, setPageSize] = useState(15);
  const [isEditing, setIsEditing] = useState(false);
  const saving = false;
  const [gridRows, setGridRows] = useState([]);
  const dirtyRowsRef = useRef(new Map());
  const apiRef = useGridApiRef();

  useEffect(() => {
    const normalizedRows = Array.isArray(rows) ? rows.map(normalizeContainerRateRow) : [];
    setGridRows(normalizedRows);
    dirtyRowsRef.current.clear();
    setIsEditing(false);
  }, [rows]);

  const markRowDirty = useCallback((newRow, oldRow) => {
    if (!newRow || !oldRow) return;
    const changed =
      newRow.fobPort !== oldRow.fobPort ||
      newRow.rate20 !== oldRow.rate20 ||
      newRow.rate40 !== oldRow.rate40 ||
      newRow.effectiveDate !== oldRow.effectiveDate;

    if (changed) {
      dirtyRowsRef.current.set(newRow.id, newRow);
    } else {
      dirtyRowsRef.current.delete(newRow.id);
    }
  }, []);

  const handleProcessRowUpdate = useCallback(
    (newRow, oldRow) => {
      if (!isEditing) return oldRow;
      setGridRows((prev) =>
        prev.map((row) => (row.id === newRow.id ? newRow : row))
      );
      markRowDirty(newRow, oldRow);
      return newRow;
    },
    [isEditing, markRowDirty]
  );

  const CustomDateFilterInput = (props) => {
    const { item, applyValue } = props;
    const [selectedDate, setSelectedDate] = useState(
      item.value ? new Date(item.value) : null
    );

    const handleChange = (newDate) => {
      const formattedDate = newDate ? format(newDate, "yyyy-MM-dd") : null;
      setSelectedDate(newDate);
      applyValue({ ...item, value: formattedDate });
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label="Select Date"
          value={selectedDate}
          onChange={(newDate) => handleChange(newDate)}
          views={["year", "month", "day"]}
          renderInput={(params) => <TextField {...params} fullWidth />}
        />
      </LocalizationProvider>
    );
  };

  const CustomDateRangeFilterInput = (props) => {
    const { item, applyValue } = props;
    const [selectedDateRangeFrom, setSelectedDateRangeFrom] = useState(
      item.value?.from ? new Date(item.value.from) : null
    );
    const [selectedDateRangeTo, setSelectedDateRangeTo] = useState(
      item.value?.to ? new Date(item.value.to) : null
    );

    const handleChange = (newFrom, newTo) => {
      if (newFrom || newTo) {
        applyValue({
          ...item,
          value: {
            from: newFrom ? format(newFrom, "yyyy-MM-dd") : null,
            to: newTo ? format(newTo, "yyyy-MM-dd") : null,
          },
        });
      }
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <DatePicker
            label="From Date"
            value={selectedDateRangeFrom}
            onChange={(newDate) => {
              setSelectedDateRangeFrom(newDate);
              handleChange(newDate, selectedDateRangeTo);
            }}
            views={["year", "month", "day"]}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
          <DatePicker
            label="To Date"
            value={selectedDateRangeTo}
            onChange={(newDate) => {
              setSelectedDateRangeTo(newDate);
              handleChange(selectedDateRangeFrom, newDate);
            }}
            views={["year", "month", "day"]}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </div>
      </LocalizationProvider>
    );
  };

  const dateFilterOperators = useMemo(
    () => [
      {
        label: "is",
        value: "is",
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.columnField || !filterItem.value) {
            return null;
          }
          const filterDate = new Date(filterItem.value);
          return ({ value }) => {
            const cellDate = new Date(value);
            return (
              cellDate.getFullYear() === filterDate.getFullYear() &&
              cellDate.getMonth() === filterDate.getMonth() &&
              cellDate.getDate() === filterDate.getDate()
            );
          };
        },
        InputComponent: CustomDateFilterInput,
      },
      {
        label: "is after",
        value: "is after",
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.columnField || !filterItem.value) {
            return null;
          }
          return ({ value }) => new Date(value) > new Date(filterItem.value);
        },
        InputComponent: CustomDateFilterInput,
      },
      {
        label: "is before",
        value: "is before",
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.columnField || !filterItem.value) {
            return null;
          }
          return ({ value }) => new Date(value) < new Date(filterItem.value);
        },
        InputComponent: CustomDateFilterInput,
      },
      {
        label: "date range",
        value: "date range",
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.columnField || !filterItem.value) {
            return null;
          }

          const { from, to } = filterItem.value;
          const fromDate = from ? new Date(from) : null;
          const toDate = to ? new Date(to) : null;

          return ({ value }) => {
            const cellDate = new Date(value);
            if (fromDate && toDate) {
              return cellDate >= fromDate && cellDate <= toDate;
            }
            if (fromDate) return cellDate >= fromDate;
            if (toDate) return cellDate <= toDate;
            return true;
          };
        },
        InputComponent: CustomDateRangeFilterInput,
      },
    ],
    []
  );

  const columns = useMemo(() => {
    const effectiveDateOperators = dateFilterOperators;
    return [
      { field: "fobPort", headerName: "FOB Port", flex: 1.2, minWidth: 160, editable: true },
      { field: "rate20", headerName: '20" Rate', flex: 1, minWidth: 130, editable: true },
      { field: "rate40", headerName: '40" Rate', flex: 1, minWidth: 130, editable: true },
      {
        field: "effectiveDate",
        headerName: "Date",
        flex: 1,
        minWidth: 150,
        editable: true,
        filterOperators: effectiveDateOperators,
      },
    ];
  }, [dateFilterOperators]);

  const handleExport = useCallback(() => {
    exportRowsToXlsx({
      columns,
      rows: gridRows,
      fileName: "container-rate.xlsx",
      sheetName: "Container Rates",
      warningMessage: "No container rates to export.",
    });
  }, [columns, gridRows]);

  const CustomPagination = () => {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Pagination
        color="primary"
        count={pageCount}
        page={page + 1}
        onChange={(_, value) => apiRef.current.setPage(value - 1)}
      />
    );
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.tableActions}>
        <Button
          variant="contained"
          size="small"
          onClick={handleExport}
          disabled={!gridRows.length || loading}
          startIcon={<DownloadIcon fontSize="small" />}
          className={classes.exportButton}
          data-testid="container-rate-export-btn"
        >
          Export
        </Button>
      </div>
      <DataGrid
        rows={gridRows}
        columns={columns}
        apiRef={apiRef}
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        pageSize={pageSize}
        rowsPerPageOptions={[15, 25, 50]}
        onPageSizeChange={(size) => setPageSize(size)}
        editMode="cell"
        autoHeight
        isCellEditable={() => false}
        processRowUpdate={handleProcessRowUpdate}
        experimentalFeatures={{ newEditingApi: true }}
        onProcessRowUpdateError={(error) => console.error(error)}
        rowHeight={32}
        columnHeaderHeight={36}
        loading={loading || saving}
        components={{ Pagination: CustomPagination }}
        sx={{
          "& .MuiDataGrid-cell": {
            fontSize: "0.78rem",
            py: 0.25,
          },
          "& .MuiDataGrid-columnHeaders": {
            fontSize: "0.82rem",
          },
        }}
      />
    </div>
  );
};

export default LandedCostContainerRateTable;
